import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('rpas_token'));
  const retryCount = useRef(0);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      setUser(response.data);
      retryCount.current = 0;
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        // Only clear token on explicit auth rejection
        localStorage.removeItem('rpas_token');
        setToken(null);
        setUser(null);
      } else {
        // Network error, timeout, 500 etc - retry up to 3 times, DON'T logout
        retryCount.current += 1;
        if (retryCount.current >= 5) {
          console.error('Auth: Too many retries, clearing session');
          localStorage.removeItem('rpas_token');
          setToken(null);
          setUser(null);
        } else {
          console.warn(`Auth: Fetch user failed (attempt ${retryCount.current}), retrying...`);
          setTimeout(() => fetchUser(), 3000);
          return; // Don't set loading=false yet
        }
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { username, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('rpas_token', newToken);
    setToken(newToken);
    setUser(userData);
    retryCount.current = 0;
    
    return userData;
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_URL}/auth/logout-track`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        });
      }
    } catch {}
    localStorage.removeItem('rpas_token');
    setToken(null);
    setUser(null);
  };

  // Heartbeat every 60s to track online status
  useEffect(() => {
    if (!token || !user) return;
    const beat = () => {
      axios.post(`${API_URL}/auth/heartbeat`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      }).catch(() => {});
    };
    beat();
    const interval = setInterval(beat, 60000);
    return () => clearInterval(interval);
  }, [token, user]);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    getAuthHeaders,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'admin',
    isSupervisor: user?.role === 'supervisor',
    isPilot: user?.role === 'pilot',
    isLeader: user?.role === 'leader',
    isVigilant: user?.role === 'vigilant',
    userProjectIds: user?.project_ids || []
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
